import { Skeleton } from 'components/ui/skeleton';
import { Typography } from 'components/ui/typography';
import {
  Container,
  Timeline,
  TimelineItem,
  TimelineDot,
  TimelineContent,
  EventDescription,
  EventMeta,
  ShipmentHeader,
  ShipmentInfo,
  ShipmentStatus,
  TrackingNumber,
  CarrierName,
  EstimatedDelivery,
  EmptyState,
  ShipmentCard,
} from './styles';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Shipment, TrackingEvent } from '@rye-com/checkout-intents-types';

type Props = {
  shipments: Shipment[];
  loading: boolean;
  error: Error | null;
  estimatedDeliveryDate?: Date;
};

const formatLocation = (location: TrackingEvent['location']): string => {
  const parts = [location.city, location.province, location.country].filter(Boolean);
  return parts.join(', ');
};

const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const hasTracking = (
  shipment: Shipment,
): shipment is Extract<Shipment, { tracking: unknown; trackingEvents: unknown[] }> => {
  return 'tracking' in shipment && 'trackingEvents' in shipment;
};

const ShipmentTimeline = ({ shipment }: { shipment: Shipment }) => {
  const status = shipment.status;

  if (!hasTracking(shipment)) {
    return (
      <ShipmentCard data-testid="tracking-events-no-tracking">
        <ShipmentHeader>
          <ShipmentInfo>
            <ShipmentStatus $status={status}>{formatStatus(status)}</ShipmentStatus>
          </ShipmentInfo>
        </ShipmentHeader>
        <EmptyState>
          <p>No tracking information available yet</p>
        </EmptyState>
      </ShipmentCard>
    );
  }

  const { tracking, trackingEvents } = shipment;

  return (
    <ShipmentCard data-testid="tracking-events-with-tracking">
      <ShipmentHeader>
        <ShipmentInfo>
          <ShipmentStatus $status={status} data-testid="shipment-status">
            {formatStatus(status)}
          </ShipmentStatus>
          {tracking.carrierName && (
            <CarrierName data-testid="carrier-name">{tracking.carrierName}</CarrierName>
          )}
          {tracking.deliveryDate?.estimated && (
            <EstimatedDelivery data-testid="estimated-delivery">
              Est. delivery: {format(new Date(tracking.deliveryDate.estimated), 'PPP hh:mm a')}
            </EstimatedDelivery>
          )}
        </ShipmentInfo>
        {tracking.number && (
          <TrackingNumber
            href={tracking.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tracking-number"
          >
            {tracking.number}
          </TrackingNumber>
        )}
      </ShipmentHeader>

      {trackingEvents.length > 0 ? (
        <Timeline data-testid="tracking-events-timeline">
          {trackingEvents.map((event, index) => {
            const location = formatLocation(event.location);
            const localTime = event.timestamp
              ? formatInTimeZone(
                  event.timestamp.local,
                  event.timestamp.local.slice(-6),
                  'PPP hh:mm a',
                )
              : null;

            return (
              <TimelineItem
                key={index}
                $isLast={index === trackingEvents.length - 1}
                data-testid={`tracking-events-timeline-item-${index}`}
              >
                <TimelineDot $status={index === 0 ? status : undefined} />
                <TimelineContent>
                  <EventDescription data-testid={`event-description-${index}`}>
                    {event.description || 'Status update'}
                  </EventDescription>
                  <EventMeta>
                    {localTime && <span data-testid={`event-date-time-${index}`}>{localTime}</span>}
                    {location && <span data-testid={`event-location-${index}`}>{location}</span>}
                  </EventMeta>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      ) : (
        <EmptyState>
          <p>No tracking events yet</p>
        </EmptyState>
      )}
    </ShipmentCard>
  );
};

const TimelineSkeleton = () => (
  <ShipmentCard>
    <ShipmentHeader>
      <ShipmentInfo>
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-32" />
      </ShipmentInfo>
      <Skeleton className="h-4 w-28" />
    </ShipmentHeader>

    <Timeline>
      {[0, 1, 2].map((index) => (
        <TimelineItem key={index} $isLast={index === 2}>
          <Skeleton className="h-[14px] w-[14px] flex-shrink-0 rounded-full" />
          <TimelineContent>
            <Skeleton className="mb-2 h-4 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  </ShipmentCard>
);

export const TrackingEventsCard = ({ shipments, loading, error, estimatedDeliveryDate }: Props) => {
  if (loading) {
    return (
      <Container className="rounded-lg border" data-testid="tracking-events-loading">
        <TimelineSkeleton />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="rounded-lg border" data-testid="tracking-events-error">
        <EmptyState>
          <Typography variant="body" as="p">
            Failed to load tracking information
          </Typography>
          <Typography variant="small" as="p">
            {error.message}
          </Typography>
        </EmptyState>
      </Container>
    );
  }

  if (shipments.length === 0) {
    return (
      <Container className="rounded-lg border" data-testid="tracking-events-empty">
        {estimatedDeliveryDate && (
          <ShipmentHeader>
            <ShipmentInfo>
              <ShipmentStatus $status="order_placed">{formatStatus('order_placed')}</ShipmentStatus>
              <EstimatedDelivery data-testid="estimated-delivery-global">
                Estimated Delivery: {format(estimatedDeliveryDate, 'PPP')}
              </EstimatedDelivery>
            </ShipmentInfo>
          </ShipmentHeader>
        )}
        <EmptyState>
          <p>No shipments found</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container className="rounded-lg border" data-testid="tracking-events-loaded">
      {shipments.map((shipment) => (
        <ShipmentTimeline key={shipment.id} shipment={shipment} />
      ))}
    </Container>
  );
};
